import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type { Page } from '@outfalak/shared'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { ago, dateTime, humanise, money, percent } from '../lib/format.js'
import {
  Badge,
  Card,
  CopyButton,
  Empty,
  Notice,
  PageHead,
  Pager,
  RowsSkeleton,
  SearchInput,
  Segmented,
  SettingRow,
  Stat,
  StatsSkeleton,
  Switch,
  Tabs,
  Ticks,
} from '../components/ui.js'
import { Donut } from '../components/charts.js'
import { GatewayKeysDrawer, type GatewayKey } from '../components/GatewayKeys.js'
import {
  CardIcon,
  DownloadIcon,
  KeyIcon,
  RazorpayIcon,
  RupeeIcon,
  ShieldIcon,
  StoreIcon,
  TruckIcon,
  UndoIcon,
  XCircleIcon,
} from '../components/icons.js'

const PER_PAGE = 25

/** What `/v1/payments` answers with, alongside the page envelope. */
interface Payment {
  id: string
  orderNumber: string
  phone: string
  customerName: string | null
  amount: string
  currency: string
  method: string
  status: string
  gateway: string
  gatewayPaymentId: string | null
  gatewayOrderId: string | null
  paidAt: string | null
  createdAt: string
  attempts: number
}

interface PaymentsPayload extends Page<Payment> {
  summary: {
    collected: string
    settled: number
    failed: number
    pending: number
    refunded: number
    successRate: number | null
  }
}

// ── Transactions ────────────────────────────────────────────────────────────

/**
 * What shoppers paid, and what the gateway said about it.
 *
 * Distinct from Orders (order-centric) and from Wallet transactions (the
 * merchant's own money). This screen exists for one question: a payment is
 * missing or disputed, what does the gateway know about it? So the gateway
 * reference is a first-class, copyable column rather than a detail.
 */
export function TransactionsPage(): React.ReactNode {
  const { shop } = useAuth()
  const [status, setStatus] = useState<'all' | 'paid' | 'cod_pending' | 'pending' | 'failed' | 'refunded'>('all')
  const [method, setMethod] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isPending, error } = useQuery({
    queryKey: ['payments', shop?.id, status, method, search, page],
    queryFn: () =>
      api<PaymentsPayload>('/v1/payments', {
        query: { status: status === 'all' ? '' : status, method, q: search, page, perPage: PER_PAGE },
      }),
    enabled: Boolean(shop?.id),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Payments belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const summary = data?.summary
  const total = data?.total ?? 0

  return (
    <>
      <PageHead
        title="Transactions"
        subtitle="What shoppers paid, and what the gateway said about it."
        crumbs={[{ label: 'Payments' }, { label: 'Transactions' }]}
        actions={
          <button type="button" className="btn" onClick={() => window.print()}>
            <DownloadIcon size={16} />
            Export
          </button>
        }
      />

      {isPending && <StatsSkeleton count={4} />}

      {summary && (
        <section className="grid grid--4">
          <Stat label="Collected" value={money(summary.collected)} icon={<RupeeIcon size={20} />} tone="var(--ok)" footnote={<span className="faint">{summary.settled} settled</span>} />
          <Stat
            label="Failed"
            value={summary.failed.toLocaleString('en-IN')}
            icon={<XCircleIcon size={20} />}
            tone="var(--bad)"
          />
          <Stat label="Pending" value={summary.pending.toLocaleString('en-IN')} icon={<CardIcon size={20} />} tone="var(--warn)" />
          <Stat
            label="Success rate"
            value={summary.successRate === null ? '—' : percent(summary.successRate)}
            icon={<ShieldIcon size={20} />}
            tone="var(--brand)"
            footnote={<span className="faint">settled of all attempted</span>}
          />
        </section>
      )}

      <Card flush>
        <Tabs
          value={status}
          onChange={(key) => reset(() => setStatus(key))}
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'paid', label: 'Paid', count: summary?.settled },
            { key: 'cod_pending', label: 'COD' },
            { key: 'pending', label: 'Pending', count: summary?.pending },
            { key: 'failed', label: 'Failed', count: summary?.failed },
            { key: 'refunded', label: 'Refunded', count: summary?.refunded },
          ]}
        />

        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={(next) => reset(() => setSearch(next))}
            placeholder="Order number, phone or gateway reference"
          />

          <select className="select" value={method} onChange={(e) => reset(() => setMethod(e.target.value))} aria-label="Method">
            <option value="">All methods</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="netbanking">Netbanking</option>
            <option value="wallet">Wallet</option>
            <option value="cod">Cash on Delivery</option>
          </select>
        </div>

        {error && (
          <Empty error title="Could not load payments">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<CardIcon size={24} />} title="No payments match those filters">
            Every payment taken through your checkout appears here.
          </Empty>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Method</th>
                    <th>Gateway reference</th>
                    <th>When</th>
                    <th className="right">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((payment) => (
                    <tr key={payment.id}>
                      <td>
                        <Link to="/orders" className="table__id">
                          {payment.orderNumber}
                        </Link>
                        {payment.attempts > 1 && (
                          <span className="table__sub" style={{ color: 'var(--warn)' }}>
                            {payment.attempts} attempts
                          </span>
                        )}
                      </td>
                      <td>
                        {payment.customerName ?? 'Guest'}
                        <span className="table__sub">{payment.phone}</span>
                      </td>
                      <td>
                        {humanise(payment.method)}
                        <span className="table__sub">{humanise(payment.gateway)}</span>
                      </td>
                      {/* The reference is why this screen exists — copyable, not
                          buried in a detail panel. */}
                      <td>
                        {payment.gatewayPaymentId ? (
                          <span className="table__cell">
                            <code style={{ fontSize: '.78rem' }}>{payment.gatewayPaymentId}</code>
                            <CopyButton value={payment.gatewayPaymentId} label="Copy reference" />
                          </span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td>
                        {ago(payment.paidAt ?? payment.createdAt)}
                        <span className="table__sub">{dateTime(payment.paidAt ?? payment.createdAt).split(',')[0]}</span>
                      </td>
                      <td className="right num">{money(payment.amount, payment.currency)}</td>
                      <td>
                        <Badge tone={payment.status}>{humanise(payment.status)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="payments"
            />
          </>
        )}
      </Card>

      <Notice kind="info" title="Three different money screens">
        This one is what shoppers paid. <strong>Orders</strong> is who bought what. <strong>Wallet → Transactions</strong> is your
        own money — commission debited and top-ups credited.
      </Notice>
    </>
  )
}
// ── Gateways ────────────────────────────────────────────────────────────────

/** What `/v1/gateways` answers with. */
interface Gateway {
  key: 'razorpay' | 'cashfree' | 'payu'
  name: string
  configured: boolean
  keyHint: string | null
  mode: string
  webhookConfigured: boolean
  volume: string
  orders: number
  share: number | null
}

/** COD has no keys and is never "active" — it sits beside whichever gateway is. */
interface CodGateway {
  key: 'cod'
  name: string
  enabled: boolean
  fee: string
  maxOrderValue: string
  volume: string
  orders: number
  share: number | null
}

interface GatewaysPayload {
  range: string
  active: string
  totalVolume: string
  gateways: Gateway[]
  cod: CodGateway
  methods: { upi: boolean; card: boolean; netbanking: boolean; wallet: boolean; cod: boolean }
}

const GATEWAY_RANGES = [
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '3m', label: '3M' },
  { key: '1y', label: '1Y' },
]

/**
 * Which gateway takes the money, and what each one has actually taken.
 *
 * Volume is grouped from settled orders rather than read from a gateway's own
 * dashboard: this is what Outfalak recorded, and it is the figure that has to
 * reconcile against the wallet. Credentials are shown as masked hints and are
 * not editable here — see the API module for why.
 */
export function GatewaysPage(): React.ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  const [range, setRange] = useState('30d')
  const [draft, setDraft] = useState<Pick<GatewaysPayload, 'active' | 'methods'> | null>(null)
  /** Which gateway's credential panel is open, if any. */
  const [editing, setEditing] = useState<GatewayKey | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['gateways', shop?.id, range],
    queryFn: () => api<GatewaysPayload>('/v1/gateways', { query: { range } }),
    enabled: Boolean(shop?.id),
  })

  const save = useMutation({
    mutationFn: (body: Pick<GatewaysPayload, 'active' | 'methods'>) =>
      api('/v1/gateways', {
        method: 'PUT',
        body: {
          paymentGateway: body.active,
          methodUpi: body.methods.upi,
          methodCard: body.methods.card,
          methodNetbanking: body.methods.netbanking,
          methodWallet: body.methods.wallet,
          methodCod: body.methods.cod,
        },
      }),
    onSuccess: () => {
      setDraft(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['gateways', shop?.id] })
    },
  })

  const current = draft ?? (data ? { active: data.active, methods: data.methods } : null)
  const dirty = draft !== null && data !== undefined && JSON.stringify(draft) !== JSON.stringify({ active: data.active, methods: data.methods })

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Gateways are configured per store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  return (
    <>
      <PageHead
        title="Payment gateways"
        subtitle="Which gateway collects your money, and what each one has taken."
        crumbs={[{ label: 'Payments' }, { label: 'Gateways' }]}
        actions={
          <>
            <Segmented label="Date range" value={range} onChange={setRange} options={GATEWAY_RANGES} />
            <button
              type="button"
              className="btn btn--primary"
              disabled={!dirty || save.isPending}
              onClick={() => current && save.mutate(current)}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      />

      {saved && <Notice kind="ok" title="Saved">New checkouts will use these settings.</Notice>}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {dirty && !save.isPending && (
        <Notice kind="warn" title="Unsaved changes">
          Checkouts keep using the current gateway until you press Save.
        </Notice>
      )}

      {/* The old note here said keys could only be set server-side, because
          storing an unverified secret silently breaks every checkout on the
          store. That reasoning still holds — what changed is that the API now
          proves a credential against the gateway before writing it, so the flow
          the note was waiting for exists. See payments/verify.ts. */}
      {editing && <GatewayKeysDrawer gateway={editing} onClose={() => setEditing(null)} />}

      <Notice kind="info" title="Keys are checked before they are saved">
        Credentials are sent to the gateway to confirm they work, and stored only if it accepts them. Secrets are never
        shown again once saved — replacing one means entering the pair again.
      </Notice>

      {error && (
        <Card>
          <Empty error title="Could not load gateways">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      )}

      {data && current && (
        <div className="grid grid--main">
          <div className="stack">
            {data.gateways.map((gateway) => {
              const isActive = current.active === gateway.key

              return (
                <Card
                  key={gateway.key}
                  title={
                    <span className="row">
                      <span
                        className="feature__icon"
                        style={{ ['--tone' as string]: gateway.configured ? 'var(--brand)' : 'var(--ink-faint)' }}
                      >
                        <RazorpayIcon size={16} />
                      </span>
                      <strong>{gateway.name}</strong>
                      {isActive && <Badge tone="brand">Active</Badge>}
                      <Badge tone={gateway.configured ? 'connected' : 'disconnected'}>
                        {gateway.configured ? 'Configured' : 'Not configured'}
                      </Badge>
                      {gateway.configured && (
                        <Badge tone={gateway.mode === 'live' ? 'ok' : 'warn'}>{gateway.mode === 'live' ? 'Live' : 'Test'}</Badge>
                      )}
                    </span>
                  }
                  action={
                    <span className="row" style={{ gap: 8 }}>
                      {!isActive && gateway.configured && (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => setDraft({ ...current, active: gateway.key })}
                        >
                          Make active
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => setEditing(gateway.key as GatewayKey)}
                      >
                        {gateway.configured ? 'Replace keys' : 'Add keys'}
                      </button>
                    </span>
                  }
                >
                  <div className="grid grid--2">
                    <dl className="kv">
                      <div className="kv__row">
                        <dt>API key</dt>
                        <dd>
                          {gateway.keyHint ? (
                            <>
                              <KeyIcon size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                              <code>{gateway.keyHint}</code>
                            </>
                          ) : (
                            <span className="faint">not set</span>
                          )}
                        </dd>
                      </div>
                      <div className="kv__row">
                        <dt>Webhook secret</dt>
                        <dd>{gateway.webhookConfigured ? <Badge tone="ok">Set</Badge> : <span className="faint">not set</span>}</dd>
                      </div>
                    </dl>

                    <dl className="kv">
                      <div className="kv__row">
                        <dt>Volume</dt>
                        <dd>{money(gateway.volume)}</dd>
                      </div>
                      <div className="kv__row">
                        <dt>Orders</dt>
                        <dd>{gateway.orders.toLocaleString('en-IN')}</dd>
                      </div>
                      <div className="kv__row">
                        <dt>Share</dt>
                        {/* Null share means nothing settled in this window at
                            all — not that this gateway took 0%. */}
                        <dd>{gateway.share === null ? <span className="faint">—</span> : percent(gateway.share)}</dd>
                      </div>
                    </dl>
                  </div>

                  {gateway.share !== null && gateway.share > 0 && (
                    <div className="meter" style={{ marginTop: 14, ['--tone' as string]: 'var(--brand)' }}>
                      <span style={{ width: `${gateway.share}%` }} />
                    </div>
                  )}
                </Card>
              )
            })}

            <Card
              title={
                <span className="row">
                  <span
                    className="feature__icon"
                    style={{ ['--tone' as string]: current.methods.cod ? 'var(--warn)' : 'var(--ink-faint)' }}
                  >
                    <TruckIcon size={16} />
                  </span>
                  <strong>{data.cod.name}</strong>
                  <Badge tone={current.methods.cod ? 'ok' : 'disconnected'}>{current.methods.cod ? 'Enabled' : 'Disabled'}</Badge>
                </span>
              }
              action={
                <Switch
                  checked={current.methods.cod}
                  label="Cash on Delivery"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, cod: v } })}
                />
              }
            >
              <div className="grid grid--2">
                <dl className="kv">
                  <div className="kv__row">
                    <dt>COD fee</dt>
                    <dd>{Number(data.cod.fee) > 0 ? money(data.cod.fee) : <span className="faint">none</span>}</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Max order value</dt>
                    <dd>{Number(data.cod.maxOrderValue) > 0 ? money(data.cod.maxOrderValue) : <span className="faint">no limit</span>}</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Fee and limits</dt>
                    <dd>
                      <Link to="/checkout/cod">Edit COD rules</Link>
                    </dd>
                  </div>
                </dl>

                <dl className="kv">
                  <div className="kv__row">
                    <dt>Volume</dt>
                    <dd>{money(data.cod.volume)}</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Orders</dt>
                    <dd>{data.cod.orders.toLocaleString('en-IN')}</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Share</dt>
                    <dd>{data.cod.share === null ? <span className="faint">—</span> : percent(data.cod.share)}</dd>
                  </div>
                </dl>
              </div>

              {data.cod.share !== null && data.cod.share > 0 && (
                <div className="meter" style={{ marginTop: 14, ['--tone' as string]: 'var(--warn)' }}>
                  <span style={{ width: `${data.cod.share}%` }} />
                </div>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card title="Payment methods" subtitle="What shoppers can choose at checkout.">
              <SettingRow title="UPI" description="Intent and collect flows.">
                <Switch
                  checked={current.methods.upi}
                  label="UPI"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, upi: v } })}
                />
              </SettingRow>
              <SettingRow title="Cards" description="Credit and debit cards.">
                <Switch
                  checked={current.methods.card}
                  label="Cards"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, card: v } })}
                />
              </SettingRow>
              <SettingRow title="Netbanking" description="Direct bank transfers.">
                <Switch
                  checked={current.methods.netbanking}
                  label="Netbanking"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, netbanking: v } })}
                />
              </SettingRow>
              <SettingRow title="Wallets" description="Paytm, PhonePe and others.">
                <Switch
                  checked={current.methods.wallet}
                  label="Wallets"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, wallet: v } })}
                />
              </SettingRow>
              <SettingRow title="Cash on Delivery" description="Shopper pays when the parcel arrives.">
                <Switch
                  checked={current.methods.cod}
                  label="Cash on Delivery"
                  onChange={(v) => setDraft({ ...current, methods: { ...current.methods, cod: v } })}
                />
              </SettingRow>
            </Card>

            {Number(data.totalVolume) > 0 && (
              <Card title="Split by gateway">
                <Donut
                  size={156}
                  centreValue={money(data.totalVolume)}
                  centreLabel="settled"
                  slices={[...data.gateways, data.cod]
                    .filter((gateway) => Number(gateway.volume) > 0)
                    .map((gateway, index) => ({
                      key: gateway.key,
                      label: gateway.name,
                      value: Number(gateway.volume),
                      color: ['var(--brand)', 'var(--ok)', 'var(--accent)'][index] ?? 'var(--warn)',
                      display: money(gateway.volume),
                    }))}
                />
              </Card>
            )}

            <Card title="How routing works" accent>
              <Ticks
                brand
                items={[
                  'One gateway is active at a time — new checkouts use it',
                  'Orders keep the gateway they were created with',
                  'Turning a method off hides it at checkout immediately',
                ]}
              />
            </Card>
          </div>
        </div>
      )}
    </>
  )
}
// ── Refunds ─────────────────────────────────────────────────────────────────

/** What `/v1/refunds` answers with, alongside the standard page envelope. */
interface Refund {
  id: string
  reference: string
  orderNumber: string
  customerName: string | null
  phone: string
  amount: string
  currency: string
  status: string
  reason: string | null
  error: string | null
  gateway: string
  createdAt: string
  processedAt: string | null
}

interface RefundsPayload extends Page<Refund> {
  summary: {
    refunded: string
    processedCount: number
    pendingCount: number
    pendingValue: string
    failedCount: number
  }
}

const REFUND_PER_PAGE = 25

/**
 * Refunds, read-only.
 *
 * Issuing one lives on the order drawer, where the amount and the reason are in
 * front of you. A list is the wrong place to move money from.
 */
export function RefundsPage(): React.ReactNode {
  const { shop } = useAuth()
  const [status, setStatus] = useState<'all' | 'pending' | 'processed' | 'failed'>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isPending, error } = useQuery({
    queryKey: ['refunds', shop?.id, status, search, page],
    queryFn: () =>
      api<RefundsPayload>('/v1/refunds', {
        query: { status: status === 'all' ? '' : status, q: search, page, perPage: REFUND_PER_PAGE },
      }),
    enabled: Boolean(shop?.id),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Refunds belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const summary = data?.summary
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / REFUND_PER_PAGE))

  return (
    <>
      <PageHead
        title="Refunds"
        subtitle="Money returned to shoppers, and where each refund currently stands."
        crumbs={[{ label: 'Payments' }, { label: 'Refunds' }]}
      />

      {isPending && <StatsSkeleton count={4} />}

      {summary && (
        <section className="grid grid--4">
          <Stat label="Refunded" value={money(summary.refunded)} icon={<UndoIcon size={20} />} tone="var(--accent)" footnote={<span className="faint">{summary.processedCount} processed</span>} />
          <Stat
            label="In progress"
            value={summary.pendingCount}
            icon={<CardIcon size={20} />}
            tone="var(--warn)"
            footnote={<span className="faint">{money(summary.pendingValue)} pending</span>}
          />
          <Stat
            label="Failed"
            value={summary.failedCount}
            icon={<XCircleIcon size={20} />}
            tone="var(--bad)"
            footnote={<span className="faint">{summary.failedCount > 0 ? 'needs attention' : 'nothing stuck'}</span>}
          />
          <Stat
            label="Total refunds"
            value={summary.processedCount + summary.pendingCount + summary.failedCount}
            icon={<ShieldIcon size={20} />}
            tone="var(--ok)"
          />
        </section>
      )}

      <Card flush>
        <Tabs
          value={status}
          onChange={(key) => reset(() => setStatus(key))}
          tabs={[
            { key: 'all', label: 'All refunds' },
            { key: 'pending', label: 'Pending', count: summary?.pendingCount },
            { key: 'processed', label: 'Processed', count: summary?.processedCount },
            { key: 'failed', label: 'Failed', count: summary?.failedCount },
          ]}
        />

        <div className="toolbar">
          <SearchInput value={search} onChange={(next) => reset(() => setSearch(next))} placeholder="Order number, reference or phone" />
        </div>

        {error && (
          <Empty error title="Could not load refunds">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={6} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<UndoIcon size={24} />} title="No refunds here">
            Refunds you issue from an order will appear on this list.
          </Empty>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Reason</th>
                    <th>Gateway</th>
                    <th>Requested</th>
                    <th className="right">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((refund) => (
                    <tr key={refund.id}>
                      <td>
                        <Link to="/orders" className="table__id">
                          {refund.orderNumber}
                        </Link>
                        <span className="table__sub">
                          <code>{refund.reference}</code>
                        </span>
                      </td>
                      <td>
                        {refund.customerName ?? 'Guest'}
                        <span className="table__sub">{refund.phone}</span>
                      </td>
                      <td>{refund.reason ?? <span className="faint">—</span>}</td>
                      <td>{humanise(refund.gateway)}</td>
                      <td>
                        {ago(refund.createdAt)}
                        <span className="table__sub">{dateTime(refund.createdAt).split(',')[0]}</span>
                      </td>
                      <td className="right num">{money(refund.amount, refund.currency)}</td>
                      <td>
                        <Badge tone={refund.status === 'processed' ? 'ok' : refund.status === 'failed' ? 'bad' : 'warn'}>
                          {humanise(refund.status)}
                        </Badge>
                        {/* The gateway's own reason for refusing. Without it a
                            failed refund is a dead end for the merchant. */}
                        {refund.error && <span className="table__sub" style={{ color: 'var(--bad)' }}>{refund.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager page={page} pageCount={pageCount} total={total} perPage={REFUND_PER_PAGE} onPage={setPage} noun="refunds" />
          </>
        )}
      </Card>

      <Card title="How refunds work">
        <ul className="list list--plain">
          <li style={{ paddingLeft: 0 }}>
            <span className="list__text">
              <strong>Prepaid orders</strong>
              <small>Refunded to the original payment method by the gateway, usually within 5–7 working days.</small>
            </span>
          </li>
          <li style={{ paddingLeft: 0 }}>
            <span className="list__text">
              <strong>COD orders</strong>
              <small>Refunded to a bank account or UPI ID the shopper provides — the money never reached the gateway.</small>
            </span>
          </li>
          <li style={{ paddingLeft: 0 }}>
            <span className="list__text">
              <strong>Commission</strong>
              <small>The platform commission on a fully refunded order is credited back to your wallet.</small>
            </span>
          </li>
        </ul>
      </Card>
    </>
  )
}
